import React, { useState } from 'react';
import { searchAPI, recruiterAPI } from '@/services/api';
import { Search, Star, MapPin, Briefcase, GraduationCap, Loader2, Mail, User } from 'lucide-react';
import Pagination from '@/components/shared/Pagination';

export default function RecruiterSearch() {
  const [jd, setJd] = useState('');
  const [req, setReq] = useState('');
  const [minMatch, setMinMatch] = useState(50);
  const [results, setResults] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState({});
  // Email search state
  const [emailSearch, setEmailSearch] = useState('');
  const [emailResult, setEmailResult] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  const searchByEmail = async (e) => {
    e.preventDefault();
    if (!emailSearch.trim()) return;
    setEmailLoading(true);
    setEmailError('');
    setEmailResult(null);
    try {
      const d = await recruiterAPI.searchCandidateByEmail(emailSearch.trim());
      setEmailResult(d);
    } catch (err) {
      setEmailError(err?.response?.data?.detail || 'No candidate found with this email');
    }
    setEmailLoading(false);
  };

  const search = async (p = 1, ps = pageSize) => {
    if (!jd.trim()) return;
    setLoading(true);
    try {
      const d = await searchAPI.aiMatch(jd, req, minMatch, p, ps);
      setResults(d);
      setPage(p);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="recruiter-search">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">AI Candidate Search</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Search by email or paste a job description to find matching candidates</p>

      {/* Email Search (Feature 4) */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5 mb-4">
        <h3 className="text-slate-900 dark:text-white font-medium text-sm mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Search by Email</h3>
        <form onSubmit={searchByEmail} className="flex gap-2">
          <input value={emailSearch} onChange={e => setEmailSearch(e.target.value)} placeholder="candidate@example.com" type="email"
            className="flex-1 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="email-search-input" />
          <button type="submit" disabled={emailLoading} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm disabled:opacity-50" data-testid="email-search-btn">
            {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </form>
        {emailError && <p className="text-red-600 dark:text-red-400 text-sm mt-2">{emailError}</p>}
        {emailResult && (
          <div className="mt-3 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid="email-search-result">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /></div>
              <div className="flex-1">
                <p className="text-slate-900 dark:text-white font-medium">{emailResult.name || 'Candidate'}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-sm">{emailResult.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {emailResult.location && <span className="text-xs text-slate-500 dark:text-zinc-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{emailResult.location}</span>}
                  <span className="text-xs text-slate-500 dark:text-zinc-400"><Briefcase className="w-3 h-3 inline" /> {emailResult.experience_count} exp</span>
                  {emailResult.industry && <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 rounded">{emailResult.industry}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${emailResult.visibility_status === 'actively_looking' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>{emailResult.visibility_status?.replace('_',' ')}</span>
                </div>
                {emailResult.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">{emailResult.skills.slice(0,8).map(s => <span key={s} className="px-2 py-0.5 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 rounded text-xs">{s}</span>)}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative flex items-center mb-4"><div className="flex-1 border-t border-slate-200 dark:border-zinc-800/50" /><span className="px-3 text-slate-500 dark:text-zinc-600 text-xs">OR</span><div className="flex-1 border-t border-slate-200 dark:border-zinc-800/50" /></div>

      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5 mb-6">
        <textarea value={jd} onChange={e => setJd(e.target.value)} rows={4} placeholder="Paste job description here..." className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-3 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-zinc-600 mb-3 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="jd-input" />
        <textarea value={req} onChange={e => setReq(e.target.value)} rows={2} placeholder="Requirements (optional)..." className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-3 text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-zinc-600 mb-3 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 dark:text-zinc-500">Min Match %</label>
            <input type="number" value={minMatch} onChange={e => setMinMatch(Number(e.target.value))} min={0} max={100} className="w-16 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-sm text-slate-900 dark:text-white" />
          </div>
          <button onClick={() => search(1)} disabled={loading || !jd.trim()} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm disabled:opacity-50" data-testid="search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {results && (
        <>
          <p className="text-slate-500 dark:text-zinc-400 text-sm mb-4">{results.total} candidates found</p>
          <div className="space-y-3">
            {results.results?.map(c => (
              <div key={c.applicant_id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5" data-testid={`candidate-${c.applicant_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-slate-900 dark:text-white font-medium">{c.name}</p>
                    <p className="text-slate-500 dark:text-zinc-500 text-sm">{c.current_title}{c.current_company && ` at ${c.current_company}`}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${c.match_percentage >= 80 ? 'text-emerald-600 dark:text-emerald-400' : c.match_percentage >= 60 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-zinc-400'}`}>{Math.round(c.match_percentage)}%</div>
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${c.visibility_status === 'actively_looking' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : c.visibility_status === 'exploring' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-500'}`}>{c.visibility_status?.replace('_', ' ')}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-zinc-500 mb-3">
                  {c.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.location}</span>}
                  {c.latest_degree && <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />{c.latest_degree}</span>}
                  <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{c.experience_count} exp</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {c.skills?.slice(0, 8).map(s => <span key={s} className="px-2 py-0.5 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 rounded text-xs">{s}</span>)}
                </div>
                {c.match_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {c.match_tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded text-xs">{t.category}: {Math.round(t.score)}%</span>)}
                  </div>
                )}
                {c.recommendation && <p className="text-slate-500 dark:text-zinc-400 text-xs italic">AI: {c.recommendation}</p>}
              </div>
            ))}
          </div>

          {results.total_pages > 1 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={results.total || 0}
              onPageChange={(p) => search(p, pageSize)}
              onPageSizeChange={(s) => { setPageSize(s); search(1, s); }}
              testIdPrefix="recruiter-search-pg"
            />
          )}
        </>
      )}
    </div>
  );
}
